import type { SubscriberRow } from '../components/Subscribers/types';
import type { CustomDataMap } from './subscriberCustomFields';
import { supabase } from './supabase';

const NICKNAME_CUSTOM_DATA_KEYS = ['nickname', 'nick_name', 'preferred_name'] as const;

function readTrimmedString(val: unknown): string | null {
  if (typeof val !== 'string') return null;
  const t = val.trim();
  return t || null;
}

function readFromCustomData(
  customData: CustomDataMap | null | undefined,
  keys: readonly string[],
): string | null {
  if (!customData || typeof customData !== 'object' || Array.isArray(customData)) return null;
  for (const key of keys) {
    const v = readTrimmedString(customData[key]);
    if (v) return v;
  }
  return null;
}

function subscriberFullName(row: SubscriberRow | null | undefined): string {
  if (!row) return '';
  const full = readTrimmedString(row.full_name ?? row.fullName);
  if (full) return full;
  return subscriberBestName(row);
}

function firstNameFromFullName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0]?.trim() ?? '';
}

export function subscriberBestEmail(row: SubscriberRow | null | undefined): string {
  if (!row) return '';
  const e = typeof row.email === 'string' ? row.email.trim() : '';
  if (e) return e;
  const alt = row.email_address ?? row.emailAddress;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

export function subscriberBestName(row: SubscriberRow | null | undefined): string {
  if (!row) return '';
  const n = typeof row.name === 'string' ? row.name.trim() : '';
  if (n) return n;
  const alt = row.full_name ?? row.fullName;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

export function subscriberDisplayLabel(row: SubscriberRow): string {
  return subscriberBestName(row) || subscriberBestEmail(row) || 'Connection';
}

/** Greeting for raw invite emails: nickname when known, else first name — not full name. */
export function subscriberInviteGreetingName(row: SubscriberRow | null | undefined): string {
  if (!row) return '';

  const fromCustom = readFromCustomData(
    row.custom_data as CustomDataMap | null | undefined,
    NICKNAME_CUSTOM_DATA_KEYS,
  );
  if (fromCustom) return fromCustom;

  const rowName = readTrimmedString(row.name);
  const rowFull = readTrimmedString(row.full_name ?? row.fullName);
  if (rowName && rowFull && rowName.toLowerCase() !== rowFull.toLowerCase()) {
    return rowName;
  }

  return firstNameFromFullName(subscriberFullName(row));
}

export function subscriberDetailPath(row: SubscriberRow): string | null {
  if (row.id != null && String(row.id).trim()) {
    return `/subscribers/${row.id}`;
  }
  const email = subscriberBestEmail(row);
  return email ? `/subscribers/${encodeURIComponent(email)}` : null;
}

function escapeIlikeTerm(term: string): string {
  return term.replace(/[%_\\]/g, '\\$&');
}

function sortConnectionsByScore(rows: SubscriberRow[]): SubscriberRow[] {
  return [...rows].sort((a, b) => {
    const aScore = typeof a.profile_score === 'number' ? a.profile_score : -1;
    const bScore = typeof b.profile_score === 'number' ? b.profile_score : -1;
    if (bScore !== aScore) return bScore - aScore;
    return subscriberDisplayLabel(a).localeCompare(subscriberDisplayLabel(b), undefined, {
      sensitivity: 'base',
    });
  });
}

/** Typeahead search for header connection lookup (email, name, role). */
export async function searchConnections(query: string, limit = 8): Promise<SubscriberRow[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const pattern = `%${escapeIlikeTerm(q)}%`;
  const { data, error } = await supabase
    .from('subscribers')
    .select(
      'id, email, name, role, funnel_status, email_status, source, profile_score, profile_score_tier',
    )
    .or(`email.ilike.${pattern},name.ilike.${pattern},role.ilike.${pattern}`)
    .order('profile_score', { ascending: false, nullsFirst: false })
    .limit(limit);

  if (error) throw error;
  return sortConnectionsByScore((data ?? []) as SubscriberRow[]);
}
